/**
 * The drawings on the feature pages.
 *
 * INLINE SVG, NOT IMAGES. These are diagrams of a mechanism rather than screenshots: a screenshot
 * ages the moment a button moves and has to be retaken for every feature, and a picture of a page
 * does not explain what the page is doing. A drawn diagram says the thing the words are saying,
 * which is what makes it worth having.
 *
 * ONE PALETTE, taken from the splash it is reached from, so a feature page reads as the same
 * product rather than as a brochure bolted on. Nothing here is interactive and nothing fetches.
 */
import * as React from "react";

const INK = "#202124";
const MUTED = "#80868B";
const LINE = "#DADCE0";
const PAPER = "#FFFFFF";
const WASH = "#F1F3F4";
const BLUE = "#1A73E8";
const GREEN = "#137333";
const AMBER = "#8A6D00";
const RED = "#C0392B";

/** A sheet of paper, the unit every one of these is drawn on. */
const Sheet = ({ x, y, w, h, fill = PAPER }: any) => (
    <rect x={x} y={y} width={w} height={h} rx="3" fill={fill} stroke={LINE} strokeWidth="1"/>
);

/** A line of text. Prose is drawn as rules rather than lettered: at this size real words are
 *  unreadable, and a reader who tries to read them is being asked to do the wrong thing. */
const Rule = ({ x, y, w, colour = LINE, h = 3 }: any) => (
    <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={colour}/>
);

const Label = ({ x, y, children, anchor = "start", colour = MUTED, size = 10, weight = 400 }: any) => (
    <text x={x} y={y} fontSize={size} fill={colour} textAnchor={anchor}
          fontFamily="Arial, Helvetica, sans-serif" fontWeight={weight}>{children}</text>
);

// SCALES TO ITS BOX rather than sitting at 520px in the middle of it: a fixed height letterboxes
// the drawing inside a wide figure and makes it read as a thumbnail. The viewBox keeps the
// proportions, so the only thing that changes is how large it is drawn.
const Frame = ({ children, height = 210 }: { children: React.ReactNode; height?: number }) => (
    <svg viewBox={`0 0 520 ${height}`} width="100%" role="img"
         style={{ display: "block", height: "auto" }}>{children}</svg>
);

/** Two columns, and the text running from the foot of one to the head of the next. */
export const ColumnsArt = () => (
    <Frame>
        <Sheet x={140} y={10} w={240} h={170}/>
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <Rule key={`l${i}`} x={155} y={30 + i * 17} w={95}/>)}
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <Rule key={`r${i}`} x={270} y={30 + i * 17} w={95}/>)}
        <path d="M250 168 C 262 182, 258 20, 268 30" fill="none" stroke={BLUE}
              strokeWidth="1.5" strokeDasharray="3 3"/>
        <polygon points="268,30 263,36 272,37" fill={BLUE}/>
        <Label x={155} y={25} colour={BLUE} size={9} weight={700}>COLUMN ONE</Label>
        <Label x={270} y={25} colour={BLUE} size={9} weight={700}>COLUMN TWO</Label>
        <Label x={260} y={200} anchor="middle">The text runs down one and on into the next</Label>
    </Frame>
);

/** A marker in the text, and its note at the foot of the page it landed on. */
export const FootnoteArt = () => (
    <Frame>
        <Sheet x={140} y={10} w={240} h={170}/>
        {[0, 1, 2].map(i => <Rule key={i} x={155} y={32 + i * 16} w={210}/>)}
        <Rule x={155} y={80} w={120}/>
        <text x={279} y={82} fontSize="8" fill={BLUE} fontFamily="Arial">1</text>
        {[0, 1].map(i => <Rule key={`b${i}`} x={155} y={98 + i * 16} w={210}/>)}
        <line x1={155} y1={138} x2={225} y2={138} stroke={INK} strokeWidth="1"/>
        <text x={157} y={154} fontSize="8" fill={BLUE} fontFamily="Arial">1</text>
        <Rule x={166} y={150} w={120} colour={MUTED}/>
        <Label x={260} y={200} anchor="middle">The note sits at the foot of the page its marker landed on</Label>
    </Frame>
);

/** A cell that contradicts its column, marked as you type. */
export const ColumnTypesArt = () => (
    <Frame>
        <Sheet x={110} y={14} w={300} h={140} fill={PAPER}/>
        <rect x={110} y={14} width={300} height={26} fill={WASH}/>
        <line x1={260} y1={14} x2={260} y2={154} stroke={LINE}/>
        {[40, 68, 96, 124].map(y => <line key={y} x1={110} y1={y} x2={410} y2={y} stroke={LINE}/>)}
        <Label x={122} y={32} colour={INK} weight={700}>Step</Label>
        <Label x={272} y={32} colour={INK} weight={700}>Date performed</Label>
        <Label x={122} y={58} colour={INK}>Load sealed</Label>
        <Label x={272} y={58} colour={INK}>2026-03-04</Label>
        <Label x={122} y={86} colour={INK}>Cycle started</Label>
        <Label x={272} y={86} colour={INK}>2026-03-04</Label>
        <Label x={122} y={114} colour={INK}>Released</Label>
        <Label x={272} y={114} colour={RED}>04/03/26</Label>
        <path d="M272 118 q 10 5 20 0 q 10 -5 20 0 q 10 5 20 0" fill="none" stroke={RED} strokeWidth="1.5"/>
        <Label x={260} y={176} anchor="middle" colour={RED}>
            A slashed date means two different days either side of the Atlantic
        </Label>
    </Frame>
);

/** Two carets in one paragraph. */
export const CoAuthoringArt = () => (
    <Frame height={170}>
        <Sheet x={140} y={14} w={240} h={120}/>
        {[0, 1, 2, 3].map(i => <Rule key={i} x={155} y={36 + i * 18} w={210}/>)}
        <rect x={236} y={50} width={2} height={13} fill={BLUE}/>
        <rect x={236} y={44} width={34} height={9} rx="2" fill={BLUE}/>
        <text x={240} y={51} fontSize="6.5" fill="#FFF" fontFamily="Arial">Amara</text>
        <rect x={190} y={86} width={2} height={13} fill={GREEN}/>
        <rect x={190} y={80} width={28} height={9} rx="2" fill={GREEN}/>
        <text x={194} y={87} fontSize="6.5" fill="#FFF" fontFamily="Arial">Tom</text>
        <Label x={260} y={158} anchor="middle">Each caret where its author is. Nothing is checked out</Label>
    </Frame>
);

/** The gate: a revision that cannot be submitted while something is unresolved. */
export const SubmissionGateArt = () => (
    <Frame height={170}>
        <Sheet x={60} y={30} w={130} h={90}/>
        {[0, 1, 2].map(i => <Rule key={i} x={72} y={48 + i * 14} w={106}/>)}
        <circle cx={178} cy={44} r={7} fill={AMBER}/>
        <text x={178} y={47} fontSize="8" fill="#FFF" textAnchor="middle" fontFamily="Arial">3</text>
        <line x1={200} y1={75} x2={290} y2={75} stroke={LINE} strokeWidth="1.5"/>
        <polygon points="290,75 283,71 283,79" fill={LINE}/>
        <rect x={300} y={52} width={150} height={46} rx="4" fill="#FCE8E6" stroke={RED}/>
        <Label x={375} y={72} anchor="middle" colour={RED} weight={700}>Not submitted</Label>
        <Label x={375} y={88} anchor="middle" colour={RED} size={9}>three cells break their column</Label>
        <Label x={260} y={146} anchor="middle">
            The refusal is the server&#8217;s, so no route round it reaches approval
        </Label>
    </Frame>
);

/** The approved vocabulary, marked where it appears. */
export const GlossaryArt = () => (
    <Frame height={170}>
        <Sheet x={140} y={14} w={240} h={110}/>
        <Rule x={155} y={36} w={210}/>
        <Rule x={155} y={54} w={80}/>
        <rect x={240} y={51} width={70} height={5} rx="2" fill="#D2E3FC"/>
        <line x1={240} y1={59} x2={310} y2={59} stroke={BLUE} strokeDasharray="2 2"/>
        <Rule x={316} y={54} w={48}/>
        <Rule x={155} y={72} w={210}/>
        <rect x={244} y={76} width={160} height={34} rx="4" fill={INK}/>
        <Label x={252} y={90} colour="#FFF" size={9} weight={700}>Accommodating Base</Label>
        <Label x={252} y={102} colour="#DADCE0" size={8}>The fluid-filled component that&#8230;</Label>
        <Label x={260} y={150} anchor="middle">
            Marked where it appears, carrying the definition somebody approved
        </Label>
    </Frame>
);

/** A revision's trail: who did what, in order. */
export const AuditArt = () => (
    <Frame height={190}>
        {[["Created", "Amara Okonkwo", GREEN],
          ["Edited", "Tom Bayliss", MUTED],
          ["Sent for approval", "Amara Okonkwo", MUTED],
          ["Signed", "Priya Raman", BLUE],
          ["Released", "Quality Director", GREEN]].map(([what, who, colour], i) => (
            <g key={what as string}>
                <circle cx={150} cy={28 + i * 32} r={4} fill={colour as string}/>
                {i < 4 && <line x1={150} y1={32 + i * 32} x2={150} y2={56 + i * 32} stroke={LINE}/>}
                <Label x={166} y={32 + i * 32} colour={INK} weight={600}>{what as string}</Label>
                <Label x={300} y={32 + i * 32}>{who as string}</Label>
            </g>
        ))}
        <Label x={260} y={180} anchor="middle">Every act, in order, with a name against it</Label>
    </Frame>
);

/** A suggestion: proposed, not made. */
export const SuggestionArt = () => (
    <Frame height={170}>
        <Sheet x={110} y={20} w={300} h={100}/>
        <Rule x={126} y={42} w={268}/>
        <g>
            <rect x={126} y={58} width={90} height={5} rx="2" fill="#FADAD7"/>
            <line x1={126} y1={60} x2={216} y2={60} stroke={RED} strokeWidth="1"/>
            <rect x={224} y={58} width={110} height={5} rx="2" fill="#CEEAD6"/>
        </g>
        <Rule x={126} y={76} w={268}/>
        <rect x={126} y={92} width={160} height={18} rx="3" fill={WASH} stroke={LINE}/>
        <Label x={134} y={104} size={9}>Accept</Label>
        <Label x={182} y={104} size={9}>Reject</Label>
        <Label x={260} y={150} anchor="middle">
            Proposed, not made — the author decides, and the trail records which
        </Label>
    </Frame>
);

/** Who may see it: the same question asked of one record. */
export const VisibilityArt = () => (
    <Frame height={180}>
        <Sheet x={210} y={16} w={100} h={70}/>
        {[0, 1, 2].map(i => <Rule key={i} x={222} y={32 + i * 13} w={76}/>)}
        {[["Quality Director", GREEN, 70],
          ["Quality Control", GREEN, 210],
          ["Supplier auditor", RED, 350]].map(([name, colour, x], i) => (
            <g key={name as string}>
                <line x1={260} y1={90} x2={(x as number) + 55} y2={116} stroke={LINE}/>
                <rect x={x as number} y={118} width={110} height={30} rx="4"
                      fill={colour === GREEN ? "#E6F4EA" : "#FCE8E6"}
                      stroke={colour as string}/>
                <Label x={(x as number) + 55} y={132} anchor="middle" colour={colour as string}
                       size={9} weight={700}>{name as string}</Label>
                <Label x={(x as number) + 55} y={143} anchor="middle" colour={colour as string}
                       size={8}>{colour === GREEN ? "may open it" : "not found"}</Label>
            </g>
        ))}
        <Label x={260} y={170} anchor="middle">
            Not permitted and not there read the same, on purpose
        </Label>
    </Frame>
);

/** The lifecycle: a revision moving through states, and where the door is locked. */
export const LifecycleArt = () => (
    <Frame height={170}>
        {[["Draft", GREEN, 40], ["In approval", AMBER, 160], ["Released", BLUE, 280], ["Superseded", MUTED, 400]]
            .map(([label, colour, x], i) => (
            <g key={label as string}>
                <rect x={x as number} y={54} width={86} height={34} rx="17"
                      fill={i === 0 ? "#E6F4EA" : i === 1 ? "#FEF7E0" : i === 2 ? "#E8F0FE" : WASH}
                      stroke={colour as string}/>
                <Label x={(x as number) + 43} y={75} anchor="middle" colour={colour as string}
                       size={10} weight={700}>{label as string}</Label>
                {i < 3 && <>
                    <line x1={(x as number) + 92} y1={71} x2={(x as number) + 112} y2={71}
                          stroke={LINE} strokeWidth="1.5"/>
                    <polygon points={`${(x as number) + 112},71 ${(x as number) + 105},67 ${(x as number) + 105},75`}
                             fill={LINE}/>
                </>}
            </g>
        ))}
        <Label x={83} y={110} anchor="middle" size={9}>you may type</Label>
        <Label x={203} y={110} anchor="middle" size={9}>read only</Label>
        <Label x={323} y={110} anchor="middle" size={9}>read only</Label>
        <Label x={443} y={110} anchor="middle" size={9}>read only</Label>
        <Label x={260} y={146} anchor="middle">
            The state decides, and the quality system owns the state
        </Label>
    </Frame>
);

/** What a signature covers: the text, digested. */
export const SignedArt = () => (
    <Frame height={180}>
        <Sheet x={70} y={20} w={150} h={110}/>
        {[0, 1, 2, 3, 4].map(i => <Rule key={i} x={84} y={42 + i * 17} w={i === 4 ? 60 : 120}/>)}
        <line x1={232} y1={75} x2={290} y2={75} stroke={LINE} strokeWidth="1.5"/>
        <polygon points="290,75 283,71 283,79" fill={LINE}/>
        <rect x={300} y={50} width={150} height={50} rx="4" fill={WASH} stroke={LINE}/>
        <Label x={375} y={70} anchor="middle" colour={MUTED} size={9} weight={700}>CONTENT DIGEST</Label>
        <text x={375} y={86} fontSize="10" fill={INK} textAnchor="middle"
              fontFamily="ui-monospace, Menlo, monospace">a3f91c7e4b02…</text>
        <Label x={260} y={156} anchor="middle">
            Change one letter and it is a different number, so the signature no longer fits
        </Label>
    </Frame>
);

/** Version history: every edit kept, with named ones standing out. */
export const HistoryArt = () => (
    <Frame height={170}>
        <line x1={60} y1={70} x2={460} y2={70} stroke={LINE} strokeWidth="1.5"/>
        {[70, 110, 150, 190, 230, 270, 310, 350, 390, 430].map((x, i) => (
            <circle key={x} cx={x} cy={70} r={i === 2 || i === 7 ? 6 : 3}
                    fill={i === 2 || i === 7 ? BLUE : LINE}/>
        ))}
        <Label x={150} y={52} anchor="middle" colour={BLUE} size={9} weight={700}>“Before review”</Label>
        <Label x={350} y={52} anchor="middle" colour={BLUE} size={9} weight={700}>“Sent to QA”</Label>
        <Label x={260} y={104} anchor="middle" size={9}>
            every save kept &#183; the named ones are the ones you will look for
        </Label>
        <Label x={260} y={140} anchor="middle">
            Restoring is a SAVE, not a rewrite &#8212; the trail keeps what was there
        </Label>
    </Frame>
);

/** The rendition: one PDF per version of the text, stamped with its state. */
export const RenditionArt = () => (
    <Frame height={180}>
        <Sheet x={160} y={16} w={200} h={130}/>
        <Label x={176} y={34} colour={MUTED} size={8}>SOP-00205 Rev 3</Label>
        <line x1={176} y1={40} x2={344} y2={40} stroke={LINE}/>
        {[0, 1, 2, 3].map(i => <Rule key={i} x={176} y={56 + i * 16} w={i === 3 ? 90 : 168}/>)}
        <text x={260} y={110} fontSize="30" fill="#EDEFF1" textAnchor="middle"
              fontFamily="Arial" fontWeight="700" transform="rotate(-20 260 110)">DRAFT</text>
        <line x1={176} y1={128} x2={344} y2={128} stroke={LINE}/>
        <Label x={176} y={140} size={7}>Peak Medical Confidential</Label>
        <Label x={344} y={140} anchor="end" size={7}>Page 1 of 4</Label>
        <Label x={260} y={168} anchor="middle">
            Built on the server, because a browser cannot number a page it has not laid out
        </Label>
    </Frame>
);

/** Search: the words, not the file name. */
export const SearchArt = () => (
    <Frame height={170}>
        <rect x={110} y={20} width={300} height={30} rx="15" fill={WASH} stroke={LINE}/>
        <circle cx={132} cy={35} r={5} fill="none" stroke={MUTED} strokeWidth="1.5"/>
        <line x1={136} y1={39} x2={140} y2={43} stroke={MUTED} strokeWidth="1.5"/>
        <Label x={150} y={39} colour={INK}>chamber interlock</Label>
        {[0, 1, 2].map(i => (
            <g key={i}>
                <Sheet x={110} y={64 + i * 30} w={300} h={24}/>
                <Label x={122} y={80 + i * 30} colour={INK} size={9} weight={600}>
                    {["SOP-00205", "SOP-00311", "WI-00042"][i]}
                </Label>
                <Rule x={188} y={76 + i * 30} w={60}/>
                <rect x={252} y={75 + i * 30} width={62} height={5} rx="2" fill="#FDE293"/>
                <Rule x={320} y={76 + i * 30} w={76}/>
            </g>
        ))}
        <Label x={260} y={158} anchor="middle">
            The text of every procedure you may open, and only those
        </Label>
    </Frame>
);

/** A cross-reference: the number is never stored, so renumbering cannot strand it. */
export const CrossReferenceArt = () => (
    <Frame height={160}>
        <Sheet x={70} y={16} w={170} h={110}/>
        <Label x={84} y={36} colour={INK} size={9} weight={700}>4. Sterilisation</Label>
        {[0, 1].map(i => <Rule key={i} x={84} y={50 + i * 14} w={140}/>)}
        <Label x={84} y={86} colour={INK} size={9} weight={700}>5. Release</Label>
        <Rule x={84} y={100} w={60}/>
        <rect x={148} y={96} width={48} height={7} rx="2" fill="#D2E3FC"/>
        <Label x={152} y={102} colour={BLUE} size={7} weight={700}>section 4</Label>
        <line x1={250} y1={70} x2={290} y2={70} stroke={LINE} strokeWidth="1.5"/>
        <polygon points="290,70 283,66 283,74" fill={LINE}/>
        <Sheet x={300} y={16} w={150} h={110}/>
        <Label x={314} y={36} colour={INK} size={9} weight={700}>3. Sterilisation</Label>
        <Label x={314} y={86} colour={INK} size={9} weight={700}>4. Release</Label>
        <Rule x={314} y={100} w={48}/>
        <rect x={366} y={96} width={48} height={7} rx="2" fill="#CEEAD6"/>
        <Label x={370} y={102} colour={GREEN} size={7} weight={700}>section 3</Label>
        <Label x={260} y={148} anchor="middle">
            A section is inserted above; the reference follows, because the number is never stored
        </Label>
    </Frame>
);

/** Text running around a figure. */
export const FigureWrapArt = () => (
    <Frame height={160}>
        <Sheet x={140} y={14} w={240} h={110}/>
        <rect x={155} y={30} width={70} height={54} rx="2" fill={WASH} stroke={LINE}/>
        <circle cx={178} cy={52} r={8} fill={LINE}/>
        <path d="M160 78 L178 58 L196 78 Z" fill={MUTED}/>
        {[0, 1, 2, 3].map(i => <Rule key={`a${i}`} x={233} y={34 + i * 14} w={132}/>)}
        {[0, 1].map(i => <Rule key={`b${i}`} x={155} y={94 + i * 14} w={210}/>)}
        <Label x={260} y={148} anchor="middle">
            The words run down its side and then back across the measure
        </Label>
    </Frame>
);

/** A running header and footer, with live fields in them. */
export const RunningLinesArt = () => (
    <Frame height={170}>
        <Sheet x={140} y={14} w={240} h={130}/>
        <rect x={140} y={14} width={240} height={22} fill={WASH}/>
        <Label x={152} y={28} colour={INK} size={8} weight={700}>SOP-00205</Label>
        <Label x={368} y={28} anchor="end" colour={BLUE} size={8}>Rev 3</Label>
        <line x1={140} y1={36} x2={380} y2={36} stroke={LINE}/>
        {[0, 1, 2, 3].map(i => <Rule key={i} x={155} y={54 + i * 16} w={i === 3 ? 90 : 210}/>)}
        <line x1={140} y1={124} x2={380} y2={124} stroke={LINE}/>
        <Label x={152} y={138} colour={MUTED} size={8}>Peak Medical Confidential</Label>
        <Label x={368} y={138} anchor="end" colour={BLUE} size={8}>Page 2 of 7</Label>
        <Label x={260} y={162} anchor="middle">
            The blue parts are live &#8212; the record fills them in, on every page
        </Label>
    </Frame>
);

/** A comment, anchored to a passage, answered. */
export const CommentArt = () => (
    <Frame height={170}>
        <Sheet x={60} y={16} w={210} h={110}/>
        {[0, 1].map(i => <Rule key={i} x={74} y={38 + i * 16} w={180}/>)}
        <rect x={74} y={70} width={120} height={5} rx="2" fill="#FDE293"/>
        {[0].map(i => <Rule key={`c${i}`} x={74} y={88} w={180}/>)}
        <rect x={290} y={24} width={170} height={44} rx="4" fill={PAPER} stroke={LINE}/>
        <circle cx={304} cy={38} r={7} fill={BLUE}/>
        <Label x={318} y={41} colour={INK} size={9} weight={600}>Priya</Label>
        <Label x={300} y={58} size={8.5}>Is 121&#176;C right for this load?</Label>
        <rect x={306} y={76} width={154} height={40} rx="4" fill={WASH} stroke={LINE}/>
        <circle cx={320} cy={90} r={7} fill={GREEN}/>
        <Label x={334} y={93} colour={INK} size={9} weight={600}>Amara</Label>
        <Label x={316} y={110} size={8.5}>Yes &#8212; see the validation report.</Label>
        <Label x={260} y={158} anchor="middle">
            Asked against the passage, answered in place, kept with the record
        </Label>
    </Frame>
);

/** A generic fallback: a sheet with a mark on it, for a feature with no drawing of its own yet. */
export const PlainArt = () => (
    <Frame height={150}>
        <Sheet x={180} y={14} w={160} h={110}/>
        {[0, 1, 2, 3, 4].map(i => <Rule key={i} x={196} y={36 + i * 17} w={i === 4 ? 70 : 128}/>)}
    </Frame>
);
